import { useEffect, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';

import { NeuSnackbar } from '@neutron/react';

import { BasicToast, ConfirmToast } from '../../components/toasts';

import { RootState } from '../../redux/store';
import { hide, setToastOptions, slideIn, slideOut } from '../../redux/actions';

import { IToastOptionsData } from '../../config/interfaces';

import './Toast.scss';

interface IToastCompProps {
  hidden: boolean;
  options: IToastOptionsData;
  slide: string;
  type: string;
}

const mapReduxStateToProps = (state: RootState) => ({
  hidden: state.ToastReducer.hidden,
  options: state.ToastReducer.options,
  slide: state.ToastReducer.slide,
  type: state.ToastReducer.type
});

export const Toast = connect(mapReduxStateToProps)(
  ({
    hidden,
    options: {
      msg,
      feedback,
      duration,
      speed,
      question,
      warning,
      handleConfirm
    },
    slide,
    type
  }: IToastCompProps) => {
    const dispatch = useDispatch();

    const backgroundColor = useMemo(() => {
      return feedback === 'info'
        ? 'neu-bg-blue-0'
        : feedback === 'success'
        ? 'neu-bg-green-0'
        : feedback === 'warning'
        ? 'neu-bg-orange-0'
        : feedback === 'error'
        ? 'neu-bg-red-0'
        : '';
    }, [feedback]);

    const styles = useMemo(
      () => ({
        show: {
          width:
            question && !(feedback === 'error' || feedback === 'success')
              ? '24em'
              : '18em',
          minHeight: '100px',
          height:
            feedback === 'error' || feedback === 'success' ? '110px' : 'auto',
          bottom: '100px',
          animation: `${
            !question ? 'toast-in-alert-bottom' : 'toast-in-question-bottom'
          } ${speed}ms`
        },
        'hide-toast': {
          width:
            question && !(feedback === 'error' || feedback === 'success')
              ? '24em'
              : '18em',
          minHeight: '100px',
          height:
            feedback === 'error' || feedback === 'success' ? '110px' : 'auto',
          bottom: question ? '-220px' : '-100px',
          animation: `${
            !question ? 'toast-out-alert-bottom' : 'toast-out-question-bottom'
          } ${speed}ms`
        }
      }),
      [feedback, speed, question]
    );

    const handleClose = () => {
      dispatch(slideOut());
      setTimeout(() => dispatch(hide()), speed + 25);
    };

    const confirm = () => {
      handleClose();
      if (handleConfirm) setTimeout(() => handleConfirm(), speed + 25);
    };

    const toastContent = useMemo(() => {
      switch (type) {
        case 'Complete':
        case 'Delete Delegated To Me':
        case 'Delete Issue':
        case 'Delete Stoplight':
        case 'Delete Note':
          return (
            <ConfirmToast
              icon={feedback !== 'none'}
              msg={msg}
              warning={warning}
              confirmText={
                type === 'Delete Stoplight' || type === 'Delete Issue'
                  ? 'Yes'
                  : type === 'Delete Delegated To Me' || type === 'Delete Note'
                  ? 'Delete'
                  : 'Complete'
              }
              closeText={
                type === 'Delete Stoplight' || type === 'Delete Note'
                  ? 'No'
                  : 'Cancel'
              }
              confirm={confirm}
              handleClose={handleClose}
            />
          );
        default:
          return (
            <BasicToast
              msg={msg}
              feedback={feedback}
              confirm={confirm}
              confirmText={question ? 'Try Again' : null}
            />
          );
      }
    }, [type, msg, feedback, warning, confirm]);

    useEffect(() => {
      let timeout: NodeJS.Timeout;
      if (!question && duration) {
        timeout = setTimeout(() => handleClose(), duration);
      }
      return () => clearTimeout(timeout);
    }, [duration, slide]);

    return (
      <div className={`custom-toast-container ${hidden ? 'hidden' : ''}`}>
        <NeuSnackbar
          className={`d-flex flex-column ${
            !question ? 'justify-content-center' : ''
          } align-items-center p-4 toast toast-shadow-primary ${backgroundColor}`}
          label=""
          kind={question || feedback === 'none' ? undefined : feedback}
          style={slide === 'show' ? styles.show : styles['hide-toast']}
          onNeuClick={undefined}
          onNeuDismiss={undefined}
        >
          {toastContent}
        </NeuSnackbar>
      </div>
    );
  }
);

const useToast = () => {
  const dispatch = useDispatch();

  const toast = (
    msg: string,
    feedback: 'info' | 'success' | 'error' | 'warning' | 'none',
    duration: number | null,
    speed: number,
    question: boolean = false,
    warning?: string | null,
    handleConfirm?: () => void
  ) => {
    dispatch(
      setToastOptions({
        msg,
        feedback,
        duration,
        speed,
        question,
        warning,
        handleConfirm
      })
    );
    dispatch(slideIn());
  };

  return { toast };
};

export default useToast;
